'use client';

import React, { useEffect, useState } from 'react';
import {
  CheckCircle,
  UserPlus,
  AlertTriangle,
  BadgeCheck,
  XCircle,
  CreditCard,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface ActivityItem {
  id: string;
  activity_type: string;
  title: string;
  subtitle: string;
  occurred_at: string;
}

const typeConfig: Record<string, { icon: React.ElementType; color: string }> = {
  job_completed: { icon: CheckCircle, color: 'text-emerald-500' },
  artisan_signup: { icon: UserPlus, color: 'text-blue-500' },
  dispute_opened: { icon: AlertTriangle, color: 'text-red-500' },
  artisan_verified: { icon: BadgeCheck, color: 'text-accent' },
  job_cancelled: { icon: XCircle, color: 'text-slate-400' },
  withdrawal: { icon: CreditCard, color: 'text-emerald-500' },
};

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function DashboardActivityFeed() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchActivity() {
      const supabase = createClient();
      const { data } = await supabase
        .from('platform_activity')
        .select('*')
        .order('occurred_at', { ascending: false })
        .limit(8);
      setActivities(data ?? []);
      setLoading(false);
    }
    fetchActivity();
  }, []);

  return (
    <div className="card-elevated rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-sm font-700 text-foreground">Recent Activity</h3>
      </div>
      <div className="divide-y divide-border/50">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={`act-skel-${i}`} className="flex items-start gap-3 px-5 py-3">
                <div className="w-3.5 h-3.5 rounded-full bg-muted animate-pulse mt-0.5 shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-muted animate-pulse rounded w-3/4" />
                  <div className="h-2.5 bg-muted animate-pulse rounded w-1/2" />
                </div>
              </div>
            ))
          : activities?.map((item) => {
              const config = typeConfig[item?.activity_type] ?? {
                icon: CheckCircle,
                color: 'text-muted-foreground',
              };
              const Icon = config.icon;
              return (
                <div
                  key={item?.id}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-muted/30 transition-colors"
                >
                  <div className="mt-0.5 shrink-0">
                    <Icon size={14} className={config.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-600 text-foreground">{item?.title}</p>
                    <p className="text-xs text-muted-foreground">{item?.subtitle}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {timeAgo(item?.occurred_at)}
                  </span>
                </div>
              );
            })}
      </div>
    </div>
  );
}